import { useAuth } from '@clerk/nextjs';
import { paragon } from '@useparagon/connect';
import { usePrevious } from '@web/hooks/usePrevious';
import { useIntegrationToken } from '@web/query/App/Paragon/useIntegrationToken';
import { useParagonLoadState } from '@web/store/App/useParagonLoadState';
import React from 'react';

function SyncParagonUserToken() {
  const { loadingState, setLoadingState } = useParagonLoadState(({ loadingState, setLoadingState }) => ({
    loadingState,
    setLoadingState,
  }));
  const { data: token, isFetched, isLoading, isError } = useIntegrationToken();

  const { orgId } = useAuth();

  const prevOrgId = usePrevious(orgId);

  React.useEffect(() => {
    let isSubscribed = true;

    if (!isFetched || isLoading || isError || !token) {
      /**
       * when the user is authenticated and the orgId changes, let's set the loading state to unauthenticated so that it
       * allows the user to authenticate again with the new orgId
       */
      if (loadingState === 'authenticated') setLoadingState('unauthenticated');
      return;
    }

    // Only attempt authentication if we're not already authenticated
    if (loadingState === 'authenticated' && prevOrgId === orgId) return;

    const getTokenAndAuthenticate = async () => {
      try {
        setLoadingState('loading');
        await paragon.authenticate(process.env.NEXT_PUBLIC_PARAGON_PROJECT_ID, token);

        // Check if component is still mounted before updating state
        if (isSubscribed) {
          setLoadingState('authenticated');
        }
      } catch (error) {
        console.error('Error authenticating with Paragon:', error);
        if (isSubscribed) {
          setLoadingState('unauthenticated');
        }
      }
    };

    getTokenAndAuthenticate();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isError, isFetched, isLoading, prevOrgId, orgId]); // Remove loadingState and setLoadingState from deps

  return null;
}

export { SyncParagonUserToken };
